import React, { createContext, useState, useLayoutEffect, useRef, useEffect, useCallback, useContext, useMemo, PureComponent } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}

function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? new Map() : undefined;

  _wrapNativeSuper = function _wrapNativeSuper(Class) {
    if (Class === null || !_isNativeFunction(Class)) return Class;

    if (typeof Class !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }

    if (typeof _cache !== "undefined") {
      if (_cache.has(Class)) return _cache.get(Class);

      _cache.set(Class, Wrapper);
    }

    function Wrapper() {
      return _construct(Class, arguments, _getPrototypeOf(this).constructor);
    }

    Wrapper.prototype = Object.create(Class.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    return _setPrototypeOf(Wrapper, Class);
  };

  return _wrapNativeSuper(Class);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var VirtuosoContext =
/*#__PURE__*/
createContext(undefined);

function combineOperators() {
  for (var _len = arguments.length, operators = new Array(_len), _key = 0; _key < _len; _key++) {
    operators[_key] = arguments[_key];
  }

  if (operators.length === 0) {
    return function (value, subscriber) {
      return subscriber(value);
    };
  }

  if (operators.length === 1) {
    return operators[0];
  }

  return function (value, subscriber) {
    var acc = function acc(value) {
      return subscriber(value);
    };

    operators.slice().reverse().forEach(function (operator) {
      var prevCallback = acc;

      acc = function acc(value) {
        return operator(value, prevCallback);
      };
    });
    acc(value);
  };
}

function buildPipe(subscribe) {
  function pipe() {
    var operator = combineOperators.apply(void 0, arguments);
    return observable(subscribe, operator);
  }

  return pipe;
}

function observable(source, operator) {
  var subscribe = function subscribe(subscriber) {
    return source(function (val) {
      return operator(val, subscriber);
    });
  };

  return {
    subscribe: subscribe,
    pipe: buildPipe(subscribe)
  };
}
function subject(initial, distinct) {
  if (distinct === void 0) {
    distinct = true;
  }

  var subscribers = [];
  var val = initial;

  var next = function next(newVal) {
    if (!distinct || newVal !== val) {
      val = newVal;
      subscribers.forEach(function (subscriber) {
        return subscriber(newVal);
      });
    }
  };

  var subscribe = function subscribe(subscriber) {
    subscribers.push(subscriber);

    if (val !== undefined) {
      subscriber(val);
    }

    return function () {
      subscribers = subscribers.filter(function (sub) {
        return sub !== subscriber;
      });
    };
  };

  return {
    next: next,
    subscribe: subscribe,
    pipe: buildPipe(subscribe),
    subscribers: subscribers
  };
}
function coldSubject() {
  var subscribers = [];

  var next = function next(newVal) {
    subscribers.forEach(function (subscriber) {
      return subscriber(newVal);
    });
  };

  var subscribe = function subscribe(subscriber) {
    subscribers.push(subscriber);
    return function () {
      subscribers = subscribers.filter(function (sub) {
        return sub !== subscriber;
      });
    };
  };

  return {
    next: next,
    subscribe: subscribe,
    pipe: buildPipe(subscribe)
  };
}
function combineLatest() {
  for (var _len2 = arguments.length, sources = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    sources[_key2] = arguments[_key2];
  }

  var called = Array(sources.length).fill(false);
  var values = Array(sources.length);
  var subscribers = [];

  var publish = function publish(subscribers) {
    called.every(function (isCalled) {
      return isCalled;
    }) && subscribers.forEach(function (subscriber) {
      return subscriber(values);
    });
  };

  sources.forEach(function (source, index) {
    source.subscribe(function (val) {
      called[index] = true;
      values[index] = val;
      publish(subscribers);
    });
  });

  var subscribe = function subscribe(subscriber) {
    subscribers.push(subscriber);
    publish([subscriber]);
    return function () {
      subscribers = subscribers.filter(function (sub) {
        return sub !== subscriber;
      });
    };
  };

  return {
    subscribe: subscribe,
    pipe: buildPipe(subscribe)
  };
}
function map(map) {
  return function (val, subscriber) {
    subscriber(map(val));
  };
}
function mapTo(val) {
  return function (_, done) {
    return done(val);
  };
}
function skip(times) {
  return function (val, done) {
    if (times > 0) {
      times--;
    } else {
      done(val);
    }
  };
}
function filter(predicate) {
  return function (val, done) {
    predicate(val) && done(val);
  };
}
function debounceTime(time) {
  var val;
  var timeout;
  return function (newVal, done) {
    val = newVal;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(function () {
      done(val);
    }, time);
  };
}
function scan(scanner, initialValue) {
  var prevVal = initialValue;
  return function (newVal, done) {
    done(prevVal = scanner(prevVal, newVal));
  };
}
function withLatestFrom() {
  for (var _len3 = arguments.length, sources = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    sources[_key3] = arguments[_key3];
  }

  var called = Array(sources.length).fill(false);
  var values = Array(sources.length);
  sources.forEach(function (source, index) {
    source.subscribe(function (val) {
      values[index] = val;
      called[index] = true;
    });
  });
  return function (val, done) {
    called.every(function (isCalled) {
      return isCalled;
    }) && done([val].concat(values));
  };
}

var NilNode =
/*#__PURE__*/
function () {
  function NilNode() {
    this.level = 0;
  }

  var _proto = NilNode.prototype;

  _proto.rebalance = function rebalance() {
    return this;
  };

  _proto.adjust = function adjust() {
    return this;
  };

  _proto.remove = function remove() {
    return this;
  };

  _proto.find = function find() {
    return;
  };

  _proto.findWith = function findWith() {
    return;
  };

  _proto.findMax = function findMax() {
    return -Infinity;
  };

  _proto.findMaxValue = function findMaxValue() {
    return;
  };

  _proto.insert = function insert(key, value) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return new NonNilNode({
      key: key,
      value: value,
      level: 1
    });
  };

  _proto.walkWithin = function walkWithin() {
    return [];
  };

  _proto.walk = function walk() {
    return [];
  };

  _proto.ranges = function ranges() {
    return [];
  };

  _proto.rangesWithin = function rangesWithin() {
    return [];
  };

  _proto.empty = function empty() {
    return true;
  };

  _proto.isSingle = function isSingle() {
    return true;
  };

  _proto.isInvariant = function isInvariant() {
    return true;
  };

  _proto.keys = function keys() {
    return [];
  };

  return NilNode;
}();

var NIL_NODE =
/*#__PURE__*/
new NilNode();
Object.freeze(NIL_NODE);

var UnreachableCaseError =
/*#__PURE__*/
function (_Error) {
  _inheritsLoose(UnreachableCaseError, _Error);

  function UnreachableCaseError(val) {
    return _Error.call(this, "Unreachable case: " + val) || this;
  }

  return UnreachableCaseError;
}(_wrapNativeSuper(Error));

var NonNilNode =
/*#__PURE__*/
function () {
  function NonNilNode(_ref) {
    var key = _ref.key,
        value = _ref.value,
        level = _ref.level,
        _ref$left = _ref.left,
        left = _ref$left === void 0 ? NIL_NODE : _ref$left,
        _ref$right = _ref.right,
        right = _ref$right === void 0 ? NIL_NODE : _ref$right;
    this.key = key;
    this.value = value;
    this.level = level;
    this.left = left;
    this.right = right;
  }

  var _proto2 = NonNilNode.prototype;

  _proto2.remove = function remove(key) {
    var left = this.left,
        right = this.right;

    if (key === this.key) {
      if (left.empty()) {
        return right;
      } else if (right.empty()) {
        return left;
      } else {
        var _left$last = left.last(),
            lastKey = _left$last[0],
            lastValue = _left$last[1];

        return this.clone({
          key: lastKey,
          value: lastValue,
          left: left.deleteLast()
        }).adjust();
      }
    } else if (key < this.key) {
      return this.clone({
        left: left.remove(key)
      }).adjust();
    } else {
      return this.clone({
        right: right.remove(key)
      }).adjust();
    }
  };

  _proto2.empty = function empty() {
    return false;
  };

  _proto2.find = function find(key) {
    if (key === this.key) {
      return this.value;
    } else if (key < this.key) {
      return this.left.find(key);
    } else {
      return this.right.find(key);
    }
  };

  _proto2.findWith = function findWith(callback) {
    var result = callback(this.value);

    switch (result) {
      case -1:
        return this.left.findWith(callback);

      case 0:
        return [this.key, this.value];

      case 1:
        return this.right.findWith(callback);

      default:
        throw new UnreachableCaseError(result);
    }
  };

  _proto2.findMax = function findMax(key) {
    if (this.key === key) {
      return key;
    }

    if (this.key < key) {
      var rightKey = this.right.findMax(key);

      if (rightKey === -Infinity) {
        return this.key;
      } else {
        return rightKey;
      }
    }

    return this.left.findMax(key);
  };

  _proto2.findMaxValue = function findMaxValue(key) {
    if (this.key === key) {
      return this.value;
    }

    if (this.key < key) {
      var rightValue = this.right.findMaxValue(key);

      if (rightValue === undefined) {
        return this.value;
      } else {
        return rightValue;
      }
    }

    return this.left.findMaxValue(key);
  };

  _proto2.insert = function insert(key, value) {
    if (key === this.key) {
      return this.clone({
        key: key,
        value: value
      });
    } else if (key < this.key) {
      return this.clone({
        left: this.left.insert(key, value)
      }).rebalance();
    } else {
      return this.clone({
        right: this.right.insert(key, value)
      }).rebalance();
    }
  };

  _proto2.walkWithin = function walkWithin(start, end) {
    var key = this.key,
        value = this.value;
    var result = [];

    if (key > start) {
      result = result.concat(this.left.walkWithin(start, end));
    }

    if (key >= start && key <= end) {
      result.push({
        key: key,
        value: value
      });
    }

    if (key <= end) {
      result = result.concat(this.right.walkWithin(start, end));
    }

    return result;
  };

  _proto2.walk = function walk() {
    return [].concat(this.left.walk(), [{
      key: this.key,
      value: this.value
    }], this.right.walk());
  };

  _proto2.last = function last() {
    if (this.right.empty()) {
      return [this.key, this.value];
    } else {
      return this.right.last();
    }
  };

  _proto2.deleteLast = function deleteLast() {
    if (this.right.empty()) {
      return this.left;
    } else {
      return this.clone({
        right: this.right.deleteLast()
      }).adjust();
    }
  };

  _proto2.clone = function clone(args) {
    return new NonNilNode({
      key: args.key !== undefined ? args.key : this.key,
      value: args.value !== undefined ? args.value : this.value,
      level: args.level !== undefined ? args.level : this.level,
      left: args.left !== undefined ? args.left : this.left,
      right: args.right !== undefined ? args.right : this.right
    });
  };

  _proto2.isSingle = function isSingle() {
    return this.level > this.right.level;
  };

  _proto2.rebalance = function rebalance() {
    return this.skew().split();
  };

  _proto2.adjust = function adjust() {
    var left = this.left,
        right = this.right,
        level = this.level;

    if (right.level >= level - 1 && left.level >= level - 1) {
      return this;
    } else if (level > right.level + 1) {
      if (left.isSingle()) {
        return this.clone({
          level: level - 1
        }).skew();
      } else {
        if (!left.empty() && !left.right.empty()) {
          return left.right.clone({
            left: left.clone({
              right: left.right.left
            }),
            right: this.clone({
              left: left.right.right,
              level: level - 1
            }),
            level: level
          });
        } else {
          throw new Error('Unexpected empty nodes');
        }
      }
    } else {
      if (this.isSingle()) {
        return this.clone({
          level: level - 1
        }).split();
      } else {
        if (!right.empty() && !right.left.empty()) {
          var rl = right.left;
          var rightLevel = rl.isSingle() ? right.level - 1 : right.level;
          return rl.clone({
            left: this.clone({
              right: rl.left,
              level: level - 1
            }),
            right: right.clone({
              left: rl.right,
              level: rightLevel
            }).split(),
            level: rl.level + 1
          });
        } else {
          throw new Error('Unexpected empty nodes');
        }
      }
    }
  };

  _proto2.isInvariant = function isInvariant() {
    var left = this.left,
        right = this.right,
        level = this.level;

    if (level !== left.level + 1) {
      return false;
    } else if (level !== right.level && level !== right.level + 1) {
      return false;
    } else if (!right.empty() && level <= right.right.level) {
      return false;
    } else {
      return left.isInvariant() && right.isInvariant();
    }
  };

  _proto2.keys = function keys() {
    return [].concat(this.left.keys(), [this.key], this.right.keys());
  };

  _proto2.ranges = function ranges() {
    return this.toRanges(this.walk());
  };

  _proto2.rangesWithin = function rangesWithin(startIndex, endIndex) {
    return this.toRanges(this.walkWithin(startIndex, endIndex));
  };

  _proto2.toRanges = function toRanges(nodes) {
    if (nodes.length === 0) {
      return [];
    }

    var first = nodes[0];
    var start = first.key,
        value = first.value;
    var result = [];

    for (var i = 1; i <= nodes.length; i++) {
      var nextNode = nodes[i];
      var end = nextNode ? nextNode.key - 1 : Infinity;
      result.push({
        start: start,
        end: end,
        value: value
      });

      if (nextNode) {
        start = nextNode.key;
        value = nextNode.value;
      }
    }

    return result;
  };

  _proto2.split = function split() {
    var right = this.right,
        level = this.level;

    if (!right.empty() && !right.right.empty() && right.level == level && right.right.level == level) {
      return right.clone({
        left: this.clone({
          right: right.left
        }),
        level: level + 1
      });
    } else {
      return this;
    }
  };

  _proto2.skew = function skew() {
    var left = this.left;

    if (!left.empty() && left.level === this.level) {
      return left.clone({
        right: this.clone({
          left: left.right
        })
      });
    } else {
      return this;
    }
  };

  return NonNilNode;
}();

var AATree =
/*#__PURE__*/
function () {
  function AATree(root) {
    this.root = root;
  }

  AATree.empty = function empty() {
    return new AATree(NIL_NODE);
  };

  var _proto3 = AATree.prototype;

  _proto3.find = function find(key) {
    return this.root.find(key);
  };

  _proto3.findMax = function findMax(key) {
    return this.root.findMax(key);
  };

  _proto3.findMaxValue = function findMaxValue(key) {
    if (this.empty()) {
      throw new Error('Searching for max value in an empty tree');
    }

    return this.root.findMaxValue(key);
  };

  _proto3.findWith = function findWith(callback) {
    return this.root.findWith(callback);
  };

  _proto3.insert = function insert(key, value) {
    return new AATree(this.root.insert(key, value));
  };

  _proto3.remove = function remove(key) {
    return new AATree(this.root.remove(key));
  };

  _proto3.empty = function empty() {
    return this.root.empty();
  };

  _proto3.keys = function keys() {
    return this.root.keys();
  };

  _proto3.walk = function walk() {
    return this.root.walk();
  };

  _proto3.walkWithin = function walkWithin(start, end) {
    var adjustedStart = this.root.findMax(start);
    return this.root.walkWithin(adjustedStart, end);
  };

  _proto3.ranges = function ranges() {
    return this.root.ranges();
  };

  _proto3.rangesWithin = function rangesWithin(start, end) {
    var adjustedStart = this.root.findMax(start);
    return this.root.rangesWithin(adjustedStart, end);
  };

  _proto3.isInvariant = function isInvariant() {
    return this.root.isInvariant();
  };

  return AATree;
}();

var OffsetList =
/*#__PURE__*/
function () {
  function OffsetList(rangeTree) {
    this.nanIndices = [];
    this.rangeTree = rangeTree;
    var offsetTree = AATree.empty();
    var offset = 0;
    var ranges = rangeTree.ranges();
    var nanFound = false;

    for (var _iterator = ranges, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var _ref2 = _ref,
          startIndex = _ref2.start,
          endIndex = _ref2.end,
          size = _ref2.value;

      if (isNaN(size)) {
        this.nanIndices.push(startIndex);

        if (!nanFound) {
          offsetTree = offsetTree.insert(offset, {
            startIndex: startIndex,
            endIndex: Infinity,
            size: size
          });
        }

        nanFound = true;
      } else if (!nanFound) {
        offsetTree = offsetTree.insert(offset, {
          startIndex: startIndex,
          endIndex: endIndex,
          size: size
        });
        offset += (endIndex - startIndex + 1) * size;
      }
    }

    this.offsetTree = offsetTree;
  }

  OffsetList.create = function create() {
    return new OffsetList(AATree.empty());
  };

  var _proto = OffsetList.prototype;

  _proto.empty = function empty() {
    return this.rangeTree.empty();
  };

  _proto.insert = function insert(start, end, size) {
    var tree = this.rangeTree;

    if (tree.empty()) {
      return new OffsetList(tree.insert(0, size));
    } // tree is in non-complete state - we know the group sizes, but not the item sizes


    if (this.nanIndices.length && this.nanIndices.indexOf(end) > -1) {
      var groupSize = tree.find(this.nanIndices[0] - 1);

      if (groupSize === size) {
        return new OffsetList(AATree.empty().insert(0, size));
      }

      for (var _iterator2 = this.nanIndices, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _iterator2[Symbol.iterator]();;) {
        var _ref3;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref3 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref3 = _i2.value;
        }

        var nanIndex = _ref3;
        tree = tree.insert(nanIndex, size);
      }

      return new OffsetList(tree);
    } // extend the range in both directions, so that we can get adjacent neighbours.
    // if the previous / next ones have the same value as the one we are about to insert,
    // we 'merge' them.


    var overlapingRanges = tree.rangesWithin(start - 1, end + 1);

    if (overlapingRanges.some(function (range) {
      return range.start === start && (range.end === end || range.end === Infinity) && range.value === size;
    })) {
      return this;
    }

    var firstPassDone = false;
    var shouldInsert = false;

    for (var _iterator3 = overlapingRanges, _isArray3 = Array.isArray(_iterator3), _i3 = 0, _iterator3 = _isArray3 ? _iterator3 : _iterator3[Symbol.iterator]();;) {
      var _ref4;

      if (_isArray3) {
        if (_i3 >= _iterator3.length) break;
        _ref4 = _iterator3[_i3++];
      } else {
        _i3 = _iterator3.next();
        if (_i3.done) break;
        _ref4 = _i3.value;
      }

      var _ref5 = _ref4,
          rangeStart = _ref5.start,
          rangeEnd = _ref5.end,
          rangeValue = _ref5.value;

      // previous range
      if (!firstPassDone) {
        shouldInsert = rangeValue !== size;
        firstPassDone = true;
      } else {
        // remove the range if it starts within the new range OR if
        // it has the same value as it, in order to perfrom a merge
        if (end >= rangeStart || size === rangeValue) {
          tree = tree.remove(rangeStart);
        }
      } // next range


      if (rangeEnd > end && end >= rangeStart) {
        if (rangeValue !== size && !isNaN(rangeValue)) {
          tree = tree.insert(end + 1, rangeValue);
        }
      }
    }

    if (shouldInsert) {
      tree = tree.insert(start, size);
    }

    return tree === this.rangeTree ? this : new OffsetList(tree);
  };

  _proto.insertSpots = function insertSpots(spotIndexes, value) {
    if (this.empty()) {
      var tree = this.rangeTree;

      for (var _iterator4 = spotIndexes, _isArray4 = Array.isArray(_iterator4), _i4 = 0, _iterator4 = _isArray4 ? _iterator4 : _iterator4[Symbol.iterator]();;) {
        var _ref6;

        if (_isArray4) {
          if (_i4 >= _iterator4.length) break;
          _ref6 = _iterator4[_i4++];
        } else {
          _i4 = _iterator4.next();
          if (_i4.done) break;
          _ref6 = _i4.value;
        }

        var spot = _ref6;
        tree = tree.insert(spot, value).insert(spot + 1, NaN);
      }

      return new OffsetList(tree);
    } else {
      throw new Error('attempting to overwrite non-empty tree');
    }
  };

  _proto.offsetOf = function offsetOf(index) {
    if (this.offsetTree.empty()) {
      return 0;
    }

    var find = function find(value) {
      if (value.startIndex > index) return -1;
      if (value.endIndex < index) return 1;
      return 0;
    };

    var offsetRange = this.offsetTree.findWith(find);

    if (offsetRange) {
      var _offset = offsetRange[0],
          _offsetRange$ = offsetRange[1],
          startIndex = _offsetRange$.startIndex,
          size = _offsetRange$.size;
      return _offset + (index - startIndex) * size;
    } else {
      throw new Error("Requested offset outside of the known ones, index: " + index);
    }
  };

  _proto.itemAt = function itemAt(index) {
    var size = this.rangeTree.findMaxValue(index);
    return {
      index: index,
      size: size,
      offset: NaN
    };
  };

  _proto.indexRange = function indexRange(startIndex, endIndex) {
    if (endIndex === 0) {
      return [];
    }

    if (this.rangeTree.empty()) {
      return [{
        index: 0,
        size: 0,
        offset: NaN
      }];
    }

    var ranges = this.rangeTree.rangesWithin(startIndex, endIndex);
    var result = [];

    for (var _iterator5 = ranges, _isArray5 = Array.isArray(_iterator5), _i5 = 0, _iterator5 = _isArray5 ? _iterator5 : _iterator5[Symbol.iterator]();;) {
      var _ref7;

      if (_isArray5) {
        if (_i5 >= _iterator5.length) break;
        _ref7 = _iterator5[_i5++];
      } else {
        _i5 = _iterator5.next();
        if (_i5.done) break;
        _ref7 = _i5.value;
      }

      var range = _ref7;
      var start = Math.max(startIndex, range.start);
      var rangeEnd = typeof range.end === 'undefined' ? Infinity : range.end;
      var end = Math.min(endIndex, rangeEnd);

      for (var i = start; i <= end; i++) {
        result.push({
          index: i,
          size: range.value,
          offset: NaN
        });
      }
    }

    return result;
  };

  _proto.range = function range(startOffset, endOffset, minIndex, maxIndex) {
    if (minIndex === void 0) {
      minIndex = 0;
    }

    if (maxIndex === void 0) {
      maxIndex = Infinity;
    }

    if (this.offsetTree.empty()) {
      return [{
        index: 0,
        size: 0,
        offset: 0
      }];
    }

    var ranges = this.offsetTree.rangesWithin(startOffset, endOffset);
    var result = [];

    for (var _iterator6 = ranges, _isArray6 = Array.isArray(_iterator6), _i6 = 0, _iterator6 = _isArray6 ? _iterator6 : _iterator6[Symbol.iterator]();;) {
      var _ref8;

      if (_isArray6) {
        if (_i6 >= _iterator6.length) break;
        _ref8 = _iterator6[_i6++];
      } else {
        _i6 = _iterator6.next();
        if (_i6.done) break;
        _ref8 = _i6.value;
      }

      var _ref9 = _ref8,
          rangeOffset = _ref9.start,
          _ref9$value = _ref9.value,
          rangeIndex = _ref9$value.startIndex,
          endIndex = _ref9$value.endIndex,
          size = _ref9$value.size;
      var _offset2 = rangeOffset;
      var startIndex = rangeIndex;

      if (rangeOffset < startOffset) {
        startIndex += Math.floor((startOffset - rangeOffset) / size);
        _offset2 += (startIndex - rangeIndex) * size;
      }

      if (startIndex < minIndex) {
        _offset2 += (minIndex - startIndex) * size;
        startIndex = minIndex;
      } // we don't know the size of this range - terminate with a probe item


      if (isNaN(size)) {
        result.push({
          index: startIndex,
          size: 0,
          offset: _offset2
        });
        return result;
      }

      endIndex = Math.min(endIndex, maxIndex);

      for (var i = startIndex; i <= endIndex; i++) {
        if (_offset2 > endOffset) {
          break;
        }

        result.push({
          index: i,
          size: size,
          offset: _offset2
        });
        _offset2 += size;
      }
    }

    return result;
  };

  _proto.total = function total(endIndex) {
    var ranges = this.rangeTree.rangesWithin(0, endIndex);
    var total = 0;

    for (var _iterator7 = ranges, _isArray7 = Array.isArray(_iterator7), _i7 = 0, _iterator7 = _isArray7 ? _iterator7 : _iterator7[Symbol.iterator]();;) {
      var _ref10;

      if (_isArray7) {
        if (_i7 >= _iterator7.length) break;
        _ref10 = _iterator7[_i7++];
      } else {
        _i7 = _iterator7.next();
        if (_i7.done) break;
        _ref10 = _i7.value;
      }

      var _ref11 = _ref10,
          start = _ref11.start,
          end = _ref11.end,
          size = _ref11.value;
      end = Math.min(end, endIndex);
      total += (end - start + 1) * (isNaN(size) ? 0 : size);
    }

    return total;
  };

  _proto.getOffsets = function getOffsets(indices) {
    var _this = this;

    var tree = AATree.empty();
    indices.forEach(function (index) {
      var offset = _this.offsetOf(index);

      tree = tree.insert(offset, index);
    });
    return new IndexList(tree);
  };

  return OffsetList;
}();
var IndexList =
/*#__PURE__*/
function () {
  function IndexList(tree) {
    this.tree = tree;
  }

  var _proto2 = IndexList.prototype;

  _proto2.findMaxValue = function findMaxValue(offset) {
    return this.tree.findMaxValue(offset);
  };

  _proto2.empty = function empty() {
    return this.tree.empty();
  };

  return IndexList;
}();

var StubIndexTransposer =
/*#__PURE__*/
function () {
  function StubIndexTransposer() {}

  var _proto = StubIndexTransposer.prototype;

  _proto.transpose = function transpose(items) {
    return items.map(function (item) {
      return {
        groupIndex: 0,
        index: item.index,
        offset: item.offset,
        size: item.size,
        transposedIndex: item.index,
        type: 'item'
      };
    });
  };

  return StubIndexTransposer;
}();
var GroupIndexTransposer =
/*#__PURE__*/
function () {
  function GroupIndexTransposer(counts) {
    this.count = counts.reduce(function (acc, groupCount) {
      return acc + groupCount + 1;
    }, 0);
    var tree = AATree.empty();
    var groupIndex = 0;
    var total = 0;

    for (var _iterator = counts, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var groupCount = _ref;
      tree = tree.insert(total, [groupIndex, total]);
      groupIndex++;
      total += groupCount + 1;
    }

    this.tree = tree;
  }

  var _proto2 = GroupIndexTransposer.prototype;

  _proto2.totalCount = function totalCount() {
    return this.count;
  };

  _proto2.transpose = function transpose(items) {
    var _this = this;

    return items.map(function (item) {
      var groupMatch = _this.tree.find(item.index);

      if (groupMatch) {
        return {
          groupIndex: groupMatch[0],
          index: item.index,
          offset: item.offset,
          size: item.size,
          type: 'group'
        };
      }

      var _this$tree$findMaxVal = _this.tree.findMaxValue(item.index),
          groupIndex = _this$tree$findMaxVal[0];

      return {
        groupIndex: groupIndex,
        index: item.index,
        offset: item.offset,
        size: item.size,
        transposedIndex: item.index - groupIndex - 1,
        type: 'item'
      };
    });
  };

  _proto2.groupIndices = function groupIndices() {
    return this.tree.keys();
  };

  return GroupIndexTransposer;
}();

function makeOutput(observable) {
  var unsubscribe;
  return function (callback) {
    if (unsubscribe) {
      unsubscribe();
    }

    if (callback) {
      unsubscribe = observable.subscribe(callback);
    }
  };
}
function makeInput(subject) {
  return subject.next;
}

var buildIsScrolling = function buildIsScrolling(scrollTop$) {
  var isScrolling$ = subject(false);
  scrollTop$.pipe(skip(1), mapTo(true)).subscribe(isScrolling$.next);
  scrollTop$.pipe(skip(1), mapTo(false), debounceTime(200)).subscribe(isScrolling$.next);
  return isScrolling$;
};

var getListTop = function getListTop(items) {
  return items.length > 0 ? items[0].offset : 0;
};

var mapToTotal = function mapToTotal(_ref) {
  var offsetList = _ref[0],
      totalCount = _ref[1];
  return offsetList.total(totalCount - 1);
};

var VirtuosoStore = function VirtuosoStore(_ref2) {
  var _ref2$overscan = _ref2.overscan,
      overscan = _ref2$overscan === void 0 ? 0 : _ref2$overscan,
      _ref2$totalCount = _ref2.totalCount,
      totalCount = _ref2$totalCount === void 0 ? 0 : _ref2$totalCount,
      itemHeight = _ref2.itemHeight;
  var viewportHeight$ = subject(0);
  var listHeight$ = subject(0);
  var scrollTop$ = subject(0);
  var footerHeight$ = subject(0);
  var itemHeights$ = subject();
  var totalCount$ = subject(totalCount);
  var groupCounts$ = subject();
  var topItemCount$ = subject();
  var initialOffsetList = OffsetList.create();
  var pendingRenderAfterInitial = false;
  var stickyItems$ = subject([]);
  var initialItemCount$ = subject();
  var scrollToIndex$ = coldSubject();

  if (itemHeight) {
    initialOffsetList = initialOffsetList.insert(0, 0, itemHeight);
  }

  var offsetList$ = subject(initialOffsetList);

  if (!itemHeight) {
    itemHeights$.pipe(withLatestFrom(offsetList$, stickyItems$)).subscribe(function (_ref3) {
      var heights = _ref3[0],
          offsetList = _ref3[1],
          stickyItems = _ref3[2];
      var newList = pendingRenderAfterInitial ? OffsetList.create() : offsetList;

      if (pendingRenderAfterInitial) {
        newList = OffsetList.create();
        pendingRenderAfterInitial = false;
      }

      for (var _iterator = heights, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
        var _ref4;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref4 = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref4 = _i.value;
        }

        var _ref5 = _ref4,
            start = _ref5.start,
            end = _ref5.end,
            size = _ref5.size;

        if (newList.empty() && start == end && stickyItems.indexOf(start) > -1) {
          newList = newList.insertSpots(stickyItems, size);
        } else {
          newList = newList.insert(start, end, size);
        }
      }

      if (newList !== offsetList) {
        offsetList$.next(newList);
      }
    });
  }

  var transposer = new StubIndexTransposer();

  var listScanner = function listScanner(overscan) {
    return function (items, _ref6) {
      var viewportHeight = _ref6[0],
          scrollTop = _ref6[1],
          topListHeight = _ref6[2],
          listHeight = _ref6[3],
          footerHeight = _ref6[4],
          minIndex = _ref6[5],
          totalCount = _ref6[6],
          offsetList = _ref6[7];
      var itemLength = items.length;

      if (totalCount === 0) {
        return [];
      }

      var listTop = getListTop(items);
      var listBottom = listTop - scrollTop + listHeight - footerHeight - topListHeight;
      var maxIndex = Math.max(totalCount - 1, 0);
      var indexOutOfAllowedRange = itemLength > 0 && (items[0].index < minIndex || items[itemLength - 1].index > maxIndex);

      if (listBottom < viewportHeight || indexOutOfAllowedRange) {
        var startOffset = Math.max(scrollTop + topListHeight, topListHeight);
        var endOffset = scrollTop + viewportHeight + overscan * 2 - 1;
        var result = transposer.transpose(offsetList.range(startOffset, endOffset, minIndex, maxIndex));
        return result;
      }

      if (listTop > scrollTop + topListHeight) {
        var _startOffset = Math.max(scrollTop + topListHeight - overscan * 2, topListHeight);

        var _endOffset = scrollTop + viewportHeight - 1;

        return transposer.transpose(offsetList.range(_startOffset, _endOffset, minIndex, maxIndex));
      }

      return items;
    };
  };

  groupCounts$.subscribe(function (counts) {
    transposer = new GroupIndexTransposer(counts);
    totalCount$.next(transposer.totalCount());
    stickyItems$.next(transposer.groupIndices());
  });
  var totalListHeight$ = combineLatest(offsetList$, totalCount$).pipe(map(mapToTotal));
  var totalHeight$ = combineLatest(totalListHeight$, footerHeight$).pipe(map(function (_ref7) {
    var totalListHeight = _ref7[0],
        footerHeight = _ref7[1];
    return totalListHeight + footerHeight;
  }));
  var stickyItemsIndexList$ = combineLatest(offsetList$, stickyItems$).pipe(map(function (_ref8) {
    var offsetList = _ref8[0],
        stickyItems = _ref8[1];
    return offsetList.getOffsets(stickyItems);
  }));
  var topList$ = subject([]);
  combineLatest(offsetList$, topItemCount$, totalCount$).pipe(filter(function (params) {
    return params[1] > 0;
  }), map(function (_ref9) {
    var offsetList = _ref9[0],
        topItemCount = _ref9[1],
        totalCount = _ref9[2];
    var endIndex = Math.max(0, Math.min(topItemCount - 1, totalCount));
    return transposer.transpose(offsetList.indexRange(0, endIndex));
  })).subscribe(topList$.next);
  combineLatest(offsetList$, stickyItemsIndexList$, scrollTop$).pipe(filter(function (params) {
    return !params[1].empty() && !params[0].empty();
  }), withLatestFrom(topList$), map(function (_ref10) {
    var _ref10$ = _ref10[0],
        offsetList = _ref10$[0],
        stickyItemsIndexList = _ref10$[1],
        scrollTop = _ref10$[2],
        topList = _ref10[1];
    var currentStickyItem = stickyItemsIndexList.findMaxValue(Math.max(scrollTop, 0));

    if (topList.length === 1 && topList[0].index === currentStickyItem) {
      return topList;
    }

    var item = offsetList.itemAt(currentStickyItem);
    return transposer.transpose([item]);
  })).subscribe(topList$.next);
  var topListHeight$ = topList$.pipe(map(function (items) {
    return items.reduce(function (total, item) {
      return total + item.size;
    }, 0);
  }));
  var minListIndex$ = topList$.pipe(map(function (topList) {
    return topList.length && topList[topList.length - 1].index + 1;
  }));
  var list$ = combineLatest(viewportHeight$, scrollTop$, topListHeight$, listHeight$, footerHeight$, minListIndex$, totalCount$, offsetList$).pipe(scan(listScanner(overscan), []));
  var endReached$ = coldSubject();
  var currentEndIndex = 0;
  list$.pipe(map(function (items) {
    return items.length ? items[items.length - 1].index : 0;
  })).pipe(withLatestFrom(totalCount$)).subscribe(function (_ref11) {
    var endIndex = _ref11[0],
        totalCount = _ref11[1];

    if (totalCount === 0) {
      return;
    }

    if (endIndex === totalCount - 1) {
      if (currentEndIndex !== endIndex) {
        currentEndIndex = endIndex;
        endReached$.next(endIndex);
      }
    }
  });
  var listOffset$ = combineLatest(list$, scrollTop$, topListHeight$).pipe(map(function (_ref12) {
    var items = _ref12[0];
    return getListTop(items);
  }));
  var scrollTo$ = scrollToIndex$.pipe(withLatestFrom(offsetList$, topListHeight$, stickyItems$, viewportHeight$, totalCount$), map(function (_ref13) {
    var location = _ref13[0],
        offsetList = _ref13[1],
        topListHeight = _ref13[2],
        stickyItems = _ref13[3],
        viewportHeight = _ref13[4],
        totalCount = _ref13[5];

    if (typeof location === 'number') {
      location = {
        index: location,
        align: 'start'
      };
    }

    var _location = location,
        index = _location.index,
        _location$align = _location.align,
        align = _location$align === void 0 ? 'start' : _location$align;
    index = Math.max(0, index, Math.min(totalCount - 1, index));
    var offset = offsetList.offsetOf(index);

    if (align == 'end') {
      offset = offset - viewportHeight + offsetList.itemAt(index).size;
    } else if (align === 'center') {
      offset = Math.round(offset - viewportHeight / 2 + offsetList.itemAt(index).size / 2);
    } else {
      if (stickyItems.indexOf(index) === -1) {
        offset -= topListHeight;
      }
    }

    return offset;
  }));
  var unsubscribeInitial = initialItemCount$.subscribe(function (count) {
    var dummyItemHeight = 30;
    itemHeights$.next([{
      start: 0,
      end: 0,
      size: dummyItemHeight
    }]);
    viewportHeight$.next(dummyItemHeight * count);
    pendingRenderAfterInitial = true;
    unsubscribeInitial();
  });
  var groupIndices$ = stickyItems$.pipe();
  var stickyItemsOffset$ = listOffset$.pipe(map(function (offset) {
    return -offset;
  }));
  var isScrolling$ = buildIsScrolling(scrollTop$);
  return {
    groupCounts: makeInput(groupCounts$),
    itemHeights: makeInput(itemHeights$),
    footerHeight: makeInput(footerHeight$),
    listHeight: makeInput(listHeight$),
    viewportHeight: makeInput(viewportHeight$),
    scrollTop: makeInput(scrollTop$),
    topItemCount: makeInput(topItemCount$),
    totalCount: makeInput(totalCount$),
    scrollToIndex: makeInput(scrollToIndex$),
    initialItemCount: makeInput(initialItemCount$),
    list: makeOutput(list$),
    topList: makeOutput(topList$),
    listOffset: makeOutput(listOffset$),
    totalHeight: makeOutput(totalHeight$),
    endReached: makeOutput(endReached$),
    isScrolling: makeOutput(isScrolling$),
    stickyItems: makeOutput(stickyItems$),
    groupIndices: makeOutput(groupIndices$),
    stickyItemsOffset: makeOutput(stickyItemsOffset$),
    scrollTo: makeOutput(scrollTo$)
  };
};

var useHeight = function useHeight(input, onMount, onResize) {
  var ref = useRef(null);
  var currentHeight = useRef(0);
  var animationFrameID = useRef(0);
  var observer = new ResizeObserver(function (entries) {
    var newHeight = entries[0].contentRect.height;

    if (currentHeight.current !== newHeight) {
      currentHeight.current = newHeight;

      if (onResize) {
        animationFrameID.current = window.requestAnimationFrame(function () {
          return onResize(entries[0].target);
        });
      }

      input(newHeight);
    }
  });

  var callbackRef = function callbackRef(elRef) {
    if (elRef) {
      observer.observe(elRef);

      if (onMount) {
        onMount(elRef);
      }

      ref.current = elRef;
    } else {
      observer.unobserve(ref.current);
      ref.current = null;
    }
  };

  useEffect(function () {
    return function () {
      return window.cancelAnimationFrame(animationFrameID.current);
    };
  }, []);
  return callbackRef;
};

function callbackToValue(output, defaultValue) {
  return function () {
    var result = defaultValue;
    output(function (val) {
      result = val;
    });
    return result;
  };
}

function useOutput(output, initialValue) {
  var _useState = useState(callbackToValue(output, initialValue)),
      value = _useState[0],
      setValue = _useState[1];

  useLayoutEffect(function () {
    output(setValue);
    return function () {
      return output(undefined);
    };
  }, []);
  return value;
}
var useSize = function useSize(callback) {
  var ref = useRef(null);
  var currentSize = useRef([0, 0]);
  var observer = new ResizeObserver(function (entries) {
    var _entries$0$contentRec = entries[0].contentRect,
        width = _entries$0$contentRec.width,
        height = _entries$0$contentRec.height;

    if (currentSize.current[0] !== width || currentSize.current[1] !== height) {
      currentSize.current = [width, height];
      callback({
        element: entries[0].target,
        width: width,
        height: height
      });
    }
  });

  var callbackRef = function callbackRef(elRef) {
    if (elRef) {
      observer.observe(elRef);
      ref.current = elRef;
    } else {
      observer.unobserve(ref.current);
      ref.current = null;
    }
  };

  return callbackRef;
};

var scrollerStyle = {
  height: '40rem',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  position: 'relative',
  outline: 'none'
};

var DefaultScrollContainer = function DefaultScrollContainer(_ref) {
  var className = _ref.className,
      style = _ref.style,
      reportScrollTop = _ref.reportScrollTop,
      scrollTo = _ref.scrollTo,
      children = _ref.children;
  var elRef = useRef(null);
  var onScroll = useCallback(function (e) {
    reportScrollTop(e.target.scrollTop);
  }, []);
  var ref = useCallback(function (theRef) {
    if (theRef) {
      theRef.addEventListener('scroll', onScroll, {
        passive: true
      });
      elRef.current = theRef;
    } else {
      elRef.current.removeEventListener('scroll', onScroll);
    }
  }, []);
  scrollTo(function (scrollTop) {
    var goTo = {
      top: scrollTop
    };
    elRef.current.scrollTo(goTo);
  });
  return React.createElement("div", {
    ref: ref,
    style: style,
    tabIndex: 0,
    className: className
  }, children);
};

var VirtuosoScroller = function VirtuosoScroller(_ref2) {
  var children = _ref2.children,
      style = _ref2.style,
      className = _ref2.className,
      _ref2$ScrollContainer = _ref2.ScrollContainer,
      ScrollContainer = _ref2$ScrollContainer === void 0 ? DefaultScrollContainer : _ref2$ScrollContainer,
      scrollTop = _ref2.scrollTop,
      scrollTo = _ref2.scrollTo;
  return React.createElement(ScrollContainer, {
    style: _extends({}, scrollerStyle, style),
    reportScrollTop: scrollTop,
    scrollTo: scrollTo,
    className: className
  }, children);
};

var VirtuosoList = function VirtuosoList(_ref) {
  var render = _ref.render,
      stickyClassName = _ref.stickyClassName;

  var _useContext = useContext(VirtuosoContext),
      topList = _useContext.topList,
      list = _useContext.list;

  var items = useOutput(list, []);
  var topItems = useOutput(topList, []);
  var renderedItems = [];
  var topOffset = 0;
  var renderedTopItemIndices = [];
  var marginTop = topItems.reduce(function (acc, item) {
    return acc + item.size;
  }, 0);
  topItems.forEach(function (item, index) {
    var itemIndex = item.index;
    renderedTopItemIndices.push(itemIndex);
    var style = {
      top: topOffset + "px",
      marginTop: index === 0 ? -marginTop + "px" : undefined
    };
    var props = {
      key: itemIndex,
      'data-index': itemIndex,
      'data-known-size': item.size,
      className: stickyClassName,
      style: style
    };
    renderedItems.push(render(item, props));
    topOffset += item.size;
  });
  items.forEach(function (item) {
    if (renderedTopItemIndices.indexOf(item.index) > -1) {
      return;
    }

    renderedItems.push(render(item, {
      key: item.index,
      'data-index': item.index,
      'data-known-size': item.size
    }));
  });
  return React.createElement(React.Fragment, null, " ", renderedItems, " ");
};

var VirtuosoStyle = function VirtuosoStyle(_ref) {
  var stickyClassName = _ref.stickyClassName;
  var style = useRef(null);
  useLayoutEffect(function () {
    var styleEl = document.createElement('style');
    document.head.appendChild(styleEl);
    var sheet = styleEl.sheet;
    sheet.insertRule("." + stickyClassName + " {\n      position: sticky;\n      position: -webkit-sticky;\n      z-index: 2;\n    } ", 0);
    style.current = styleEl;
    return function () {
      document.head.removeChild(style.current);
      style.current = null;
    };
  }, []);
  return null;
};
var START_CHAR = 97;
var END_CHAR = 122;

var randomChar = function randomChar() {
  return String.fromCharCode(Math.round(Math.random() * (END_CHAR - START_CHAR) + START_CHAR));
};

var randomClassName = function randomClassName() {
  return new Array(12).fill(0).map(randomChar).join('');
};
var viewportStyle = {
  top: 0,
  position: 'absolute',
  height: '100%',
  width: '100%',
  overflow: 'absolute'
};

var VirtuosoFiller = function VirtuosoFiller(_ref) {
  var height = _ref.height;
  return React.createElement("div", {
    style: {
      height: height + "px",
      position: 'absolute',
      top: 0
    }
  }, "\xA0");
};

var DefaultFooterContainer = function DefaultFooterContainer(_ref) {
  var children = _ref.children,
      footerRef = _ref.footerRef;
  return React.createElement("footer", {
    ref: footerRef
  }, children);
};
var DefaultListContainer = function DefaultListContainer(_ref2) {
  var children = _ref2.children,
      listRef = _ref2.listRef,
      style = _ref2.style;
  return React.createElement("div", {
    ref: listRef,
    style: style
  }, children);
};

var VirtuosoFooter = function VirtuosoFooter(_ref3) {
  var footer = _ref3.footer,
      _ref3$FooterContainer = _ref3.FooterContainer,
      FooterContainer = _ref3$FooterContainer === void 0 ? DefaultFooterContainer : _ref3$FooterContainer;
  var footerCallbackRef = useHeight(useContext(VirtuosoContext).footerHeight);
  return React.createElement(FooterContainer, {
    footerRef: footerCallbackRef
  }, footer());
};

var getHeights = function getHeights(children) {
  var results = [];

  for (var i = 0, len = children.length; i < len; i++) {
    var child = children.item(i);

    if (!child || child.dataset.index === undefined) {
      continue;
    }

    var index = parseInt(child.dataset.index);
    var knownSize = parseInt(child.dataset.knownSize);
    var size = child.offsetHeight;

    if (size === knownSize) {
      continue;
    }

    var lastResult = results[results.length - 1];

    if (results.length === 0 || lastResult.size !== size || lastResult.end !== index - 1) {
      results.push({
        start: index,
        end: index,
        size: size
      });
    } else {
      results[results.length - 1].end++;
    }
  }

  return results;
};

var ListWrapper = function ListWrapper(_ref4) {
  var fixedItemHeight = _ref4.fixedItemHeight,
      children = _ref4.children,
      ListContainer = _ref4.ListContainer;

  var _useContext = useContext(VirtuosoContext),
      listHeight = _useContext.listHeight,
      itemHeights = _useContext.itemHeights,
      listOffset = _useContext.listOffset;

  var translate = useOutput(listOffset, 0);
  var style = {
    marginTop: translate + "px"
  };
  var listCallbackRef = useHeight(listHeight, function () {}, function (ref) {
    if (!fixedItemHeight) {
      var measuredItemHeights = getHeights(ref.children);

      if (measuredItemHeights.length > 0) {
        itemHeights(measuredItemHeights);
      }
    }
  });
  return React.createElement(ListContainer, {
    listRef: listCallbackRef,
    style: style
  }, children);
};

var VirtuosoView = function VirtuosoView(_ref5) {
  var style = _ref5.style,
      footer = _ref5.footer,
      item = _ref5.item,
      fixedItemHeight = _ref5.fixedItemHeight,
      ScrollContainer = _ref5.ScrollContainer,
      ListContainer = _ref5.ListContainer,
      FooterContainer = _ref5.FooterContainer,
      className = _ref5.className;

  var _useContext2 = useContext(VirtuosoContext),
      scrollTo = _useContext2.scrollTo,
      scrollTop = _useContext2.scrollTop,
      totalHeight = _useContext2.totalHeight,
      viewportHeight = _useContext2.viewportHeight;

  var fillerHeight = useOutput(totalHeight, 0);
  var stickyClassName = useMemo(randomClassName, []);
  var viewportCallbackRef = useHeight(viewportHeight);
  return React.createElement(VirtuosoScroller, {
    style: style,
    ScrollContainer: ScrollContainer,
    className: className,
    scrollTo: scrollTo,
    scrollTop: scrollTop
  }, React.createElement("div", {
    ref: viewportCallbackRef,
    style: viewportStyle
  }, React.createElement(ListWrapper, {
    fixedItemHeight: fixedItemHeight,
    ListContainer: ListContainer
  }, React.createElement(VirtuosoList, {
    render: item,
    stickyClassName: stickyClassName
  }), footer && React.createElement(VirtuosoFooter, {
    footer: footer,
    FooterContainer: FooterContainer
  }))), React.createElement(VirtuosoFiller, {
    height: fillerHeight
  }), React.createElement(VirtuosoStyle, Object.assign({}, {
    stickyClassName: stickyClassName
  })));
};

var VirtuosoPresentation = function VirtuosoPresentation(_ref) {
  var contextValue = _ref.contextValue,
      style = _ref.style,
      className = _ref.className,
      item = _ref.item,
      footer = _ref.footer,
      itemHeight = _ref.itemHeight,
      ScrollContainer = _ref.ScrollContainer,
      ListContainer = _ref.ListContainer,
      FooterContainer = _ref.FooterContainer;
  return React.createElement(VirtuosoContext.Provider, {
    value: contextValue
  }, React.createElement(VirtuosoView, {
    style: style || {},
    className: className,
    item: item,
    footer: footer,
    fixedItemHeight: itemHeight !== undefined,
    ScrollContainer: ScrollContainer,
    FooterContainer: FooterContainer,
    ListContainer: ListContainer || DefaultListContainer
  }));
};
var Virtuoso =
/*#__PURE__*/
function (_PureComponent) {
  _inheritsLoose(Virtuoso, _PureComponent);

  function Virtuoso(props) {
    var _this;

    _this = _PureComponent.call(this, props) || this;

    _this.itemRender = function (item, _ref2) {
      var key = _ref2.key,
          props = _objectWithoutPropertiesLoose(_ref2, ["key"]);

      var _this$props = _this.props,
          computeItemKey = _this$props.computeItemKey,
          itemRender = _this$props.item,
          _this$props$ItemConta = _this$props.ItemContainer,
          ItemContainer = _this$props$ItemConta === void 0 ? 'div' : _this$props$ItemConta;

      if (computeItemKey) {
        key = computeItemKey(item.index);
      }

      return React.createElement(ItemContainer, _extends({}, props, {
        key: key
      }), itemRender(item.index));
    };

    _this.state = VirtuosoStore(props);
    return _this;
  }

  Virtuoso.getDerivedStateFromProps = function getDerivedStateFromProps(props, state) {
    state.isScrolling(props.scrollingStateChange);
    state.endReached(props.endReached);
    state.topItemCount(props.topItems || 0);
    state.totalCount(props.totalCount);
    props.initialItemCount && state.initialItemCount(props.initialItemCount);
    return null;
  };

  var _proto = Virtuoso.prototype;

  _proto.scrollToIndex = function scrollToIndex(location) {
    this.state.scrollToIndex(location);
  };

  _proto.render = function render() {
    return React.createElement(VirtuosoPresentation, {
      contextValue: this.state,
      style: this.props.style,
      className: this.props.className,
      item: this.itemRender,
      footer: this.props.footer,
      itemHeight: this.props.itemHeight,
      ScrollContainer: this.props.ScrollContainer,
      FooterContainer: this.props.FooterContainer,
      ListContainer: this.props.ListContainer
    });
  };

  return Virtuoso;
}(PureComponent);

var GroupedVirtuoso =
/*#__PURE__*/
function (_PureComponent) {
  _inheritsLoose(GroupedVirtuoso, _PureComponent);

  function GroupedVirtuoso(props) {
    var _this;

    _this = _PureComponent.call(this, props) || this;

    _this.itemRender = function (item, props) {
      var ItemContainer = _this.props.ItemContainer;
      var GroupContainer = _this.props.GroupContainer || ItemContainer;

      if (item.type == 'group') {
        var children = _this.props.group(item.groupIndex);

        if (GroupContainer) {
          return React.createElement(GroupContainer, Object.assign({
            key: props.key
          }, props), children);
        } else {
          return React.createElement("div", Object.assign({}, props), children);
        }
      } else {
        var _children = _this.props.item(item.transposedIndex, item.groupIndex);

        if (ItemContainer) {
          return React.createElement(ItemContainer, Object.assign({
            key: props.key
          }, props), _children);
        } else {
          return React.createElement("div", Object.assign({}, props), _children);
        }
      }
    };

    _this.state = VirtuosoStore(props);
    return _this;
  }

  GroupedVirtuoso.getDerivedStateFromProps = function getDerivedStateFromProps(props, state) {
    state.endReached(props.endReached);
    state.isScrolling(props.scrollingStateChange);
    state.groupCounts(props.groupCounts);
    state.groupIndices(props.groupIndices);
    return null;
  };

  var _proto = GroupedVirtuoso.prototype;

  _proto.scrollToIndex = function scrollToIndex(location) {
    this.state.scrollToIndex(location);
  };

  _proto.render = function render() {
    return React.createElement(VirtuosoPresentation, {
      contextValue: this.state,
      style: this.props.style,
      className: this.props.className,
      item: this.itemRender,
      footer: this.props.footer,
      itemHeight: this.props.itemHeight,
      ScrollContainer: this.props.ScrollContainer,
      FooterContainer: this.props.FooterContainer,
      ListContainer: this.props.ListContainer
    });
  };

  return GroupedVirtuoso;
}(PureComponent);

var ceil = Math.ceil,
    floor = Math.floor,
    min = Math.min,
    max = Math.max;

var hackFloor = function hackFloor(val) {
  return ceil(val) - val < 0.03 ? ceil(val) : floor(val);
};

var VirtuosoGridEngine = function VirtuosoGridEngine() {
  var gridDimensions$ = subject([0, 0, undefined, undefined]);
  var totalCount$ = subject(0);
  var scrollTop$ = subject(0);
  var overscan$ = subject(0);
  var itemRange$ = subject([0, 0]);
  var totalHeight$ = subject(0);
  var listOffset$ = subject(0);
  var scrollToIndex$ = coldSubject();
  combineLatest(gridDimensions$, scrollTop$, overscan$, totalCount$).pipe(withLatestFrom(itemRange$)).subscribe(function (_ref) {
    var _ref$ = _ref[0],
        _ref$$ = _ref$[0],
        viewportWidth = _ref$$[0],
        viewportHeight = _ref$$[1],
        itemWidth = _ref$$[2],
        itemHeight = _ref$$[3],
        scrollTop = _ref$[1],
        overscan = _ref$[2],
        totalCount = _ref$[3],
        itemRange = _ref[1];

    if (itemWidth === undefined || itemHeight === undefined) {
      return;
    }

    if (totalCount == 0) {
      itemRange$.next([0, -1]);
      listOffset$.next(0);
      return;
    }

    var startIndex = itemRange[0],
        endIndex = itemRange[1];
    var itemsPerRow = hackFloor(viewportWidth / itemWidth);

    var toRowIndex = function toRowIndex(index, roundFunc) {
      if (roundFunc === void 0) {
        roundFunc = floor;
      }

      return roundFunc(index / itemsPerRow);
    };

    var updateRange = function updateRange(down) {
      var _ref2 = down ? [0, overscan] : [overscan, 0],
          topOverscan = _ref2[0],
          bottomOverscan = _ref2[1];

      var startIndex = itemsPerRow * floor((scrollTop - topOverscan) / itemHeight);
      var endIndex = itemsPerRow * ceil((scrollTop + viewportHeight + bottomOverscan) / itemHeight) - 1;
      endIndex = min(totalCount - 1, endIndex);
      startIndex = min(endIndex, max(0, startIndex));
      itemRange$.next([startIndex, endIndex]);
      listOffset$.next(toRowIndex(startIndex) * itemHeight);
    };

    var listTop = itemHeight * toRowIndex(startIndex);
    var listBottom = itemHeight * toRowIndex(endIndex) + itemHeight; // totalCount has decreased, we have to re-render

    if (totalCount < endIndex - 1) {
      updateRange(true); // user is scrolling up - list top is below the top edge of the viewport
    } else if (listTop > scrollTop) {
      updateRange(false); // user is scrolling down - list bottom is above the bottom edge of the viewport
    } else if (listBottom < scrollTop + viewportHeight) {
      updateRange(true);
    }

    totalHeight$.next(itemHeight * toRowIndex(totalCount, ceil));
  });
  var scrollTo$ = scrollToIndex$.pipe(withLatestFrom(gridDimensions$, totalCount$), map(function (_ref3) {
    var location = _ref3[0],
        _ref3$ = _ref3[1],
        viewportWidth = _ref3$[0],
        viewportHeight = _ref3$[1],
        itemWidth = _ref3$[2],
        itemHeight = _ref3$[3],
        totalCount = _ref3[2];

    if (itemWidth === undefined || itemHeight === undefined) {
      return 0;
    }

    if (typeof location === 'number') {
      location = {
        index: location,
        align: 'start'
      };
    }

    var _location = location,
        index = _location.index,
        _location$align = _location.align,
        align = _location$align === void 0 ? 'start' : _location$align;
    index = Math.max(0, index, Math.min(totalCount - 1, index));
    var itemsPerRow = hackFloor(viewportWidth / itemWidth);
    var offset = floor(index / itemsPerRow) * itemHeight;

    if (align == 'end') {
      offset = offset - viewportHeight + itemHeight;
    } else if (align === 'center') {
      offset = Math.round(offset - viewportHeight / 2 + itemHeight / 2);
    }

    return offset;
  }));
  var isScrolling$ = buildIsScrolling(scrollTop$);
  var endReached$ = coldSubject();
  var currentEndIndex = 0;
  itemRange$.pipe(withLatestFrom(totalCount$)).subscribe(function (_ref4) {
    var _ref4$ = _ref4[0],
        _ = _ref4$[0],
        endIndex = _ref4$[1],
        totalCount = _ref4[1];

    if (totalCount === 0) {
      return;
    }

    if (endIndex === totalCount - 1) {
      if (currentEndIndex !== endIndex) {
        currentEndIndex = endIndex;
        endReached$.next(endIndex);
      }
    }
  });
  return {
    gridDimensions: makeInput(gridDimensions$),
    totalCount: makeInput(totalCount$),
    scrollTop: makeInput(scrollTop$),
    overscan: makeInput(overscan$),
    scrollToIndex: makeInput(scrollToIndex$),
    itemRange: makeOutput(itemRange$),
    totalHeight: makeOutput(totalHeight$),
    listOffset: makeOutput(listOffset$),
    scrollTo: makeOutput(scrollTo$),
    isScrolling: makeOutput(isScrolling$),
    endReached: makeOutput(endReached$)
  };
};

var VirtuosoGrid =
/*#__PURE__*/
function (_React$PureComponent) {
  _inheritsLoose(VirtuosoGrid, _React$PureComponent);

  function VirtuosoGrid() {
    var _this;

    _this = _React$PureComponent.apply(this, arguments) || this;
    _this.state = VirtuosoGridEngine();
    return _this;
  }

  VirtuosoGrid.getDerivedStateFromProps = function getDerivedStateFromProps(props, engine) {
    engine.overscan(props.overscan || 0);
    engine.totalCount(props.totalCount);
    engine.isScrolling(props.scrollingStateChange);
    engine.endReached(props.endReached);
    return null;
  };

  var _proto = VirtuosoGrid.prototype;

  _proto.scrollToIndex = function scrollToIndex(location) {
    this.state.scrollToIndex(location);
  };

  _proto.render = function render() {
    return React.createElement(VirtuosoGridFC, Object.assign({}, this.props, {
      engine: this.state
    }));
  };

  return VirtuosoGrid;
}(React.PureComponent);

var buildItems = function buildItems(_ref, item, itemClassName, ItemContainer) {
  var startIndex = _ref[0],
      endIndex = _ref[1];
  var items = [];

  for (var index = startIndex; index <= endIndex; index++) {
    items.push(React.createElement(ItemContainer, {
      key: index,
      className: itemClassName
    }, item(index)));
  }

  return items;
};

var VirtuosoGridFC = function VirtuosoGridFC(_ref2) {
  var ScrollContainer = _ref2.ScrollContainer,
      _ref2$ItemContainer = _ref2.ItemContainer,
      ItemContainer = _ref2$ItemContainer === void 0 ? 'div' : _ref2$ItemContainer,
      _ref2$ListContainer = _ref2.ListContainer,
      ListContainer = _ref2$ListContainer === void 0 ? 'div' : _ref2$ListContainer,
      className = _ref2.className,
      item = _ref2.item,
      _ref2$itemClassName = _ref2.itemClassName,
      itemClassName = _ref2$itemClassName === void 0 ? 'virtuoso-grid-item' : _ref2$itemClassName,
      _ref2$listClassName = _ref2.listClassName,
      listClassName = _ref2$listClassName === void 0 ? 'virtuoso-grid-list' : _ref2$listClassName,
      engine = _ref2.engine,
      _ref2$style = _ref2.style,
      style = _ref2$style === void 0 ? {
    height: '40rem'
  } : _ref2$style;
  var itemRange = engine.itemRange,
      listOffset = engine.listOffset,
      totalHeight = engine.totalHeight,
      gridDimensions = engine.gridDimensions,
      scrollTo = engine.scrollTo,
      scrollTop = engine.scrollTop;
  var fillerHeight = useOutput(totalHeight, 0);
  var translate = useOutput(listOffset, 0);
  var listStyle = {
    marginTop: translate + "px"
  };
  var itemIndexRange = useOutput(itemRange, [0, 0]);
  var viewportCallbackRef = useSize(function (_ref3) {
    var element = _ref3.element,
        width = _ref3.width,
        height = _ref3.height;
    var firstItem = element.firstChild.firstChild;
    gridDimensions([width, height, firstItem.offsetWidth, firstItem.offsetHeight]);
  });
  return React.createElement(VirtuosoScroller, {
    style: style,
    ScrollContainer: ScrollContainer,
    className: className,
    scrollTo: scrollTo,
    scrollTop: scrollTop
  }, React.createElement("div", {
    ref: viewportCallbackRef,
    style: viewportStyle
  }, React.createElement(ListContainer, {
    style: listStyle,
    className: listClassName
  }, buildItems(itemIndexRange, item, itemClassName, ItemContainer))), React.createElement(VirtuosoFiller, {
    height: fillerHeight
  }));
};

export { GroupedVirtuoso, Virtuoso, VirtuosoGrid, VirtuosoPresentation };
//# sourceMappingURL=react-virtuoso.esm.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/react-virtuoso/dist/react-virtuoso.esm.js
// module id = 2659
// module chunks = 0